import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  BookOpen,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  EyeOff,
  Loader2,
  Save,
  ShoppingBag,
  Unplug,
} from 'lucide-react';
import { authApi, onbuyApi } from '@/services/api';
import { ONBUY_UK_SITE_ID } from '@/components/settings/setupGuides';

/**
 * OnBuy Marketplaces configure panel — Seller API keys + how-to guide.
 */
export default function OnBuyIntegrationTab() {
  const qc = useQueryClient();
  const [showSecret, setShowSecret] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [guideOpen, setGuideOpen] = useState(true);
  const [copied, setCopied] = useState<'site' | null>(null);

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => (await authApi.getCompanyProfile()).data.message,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const features: string[] = Array.isArray(profile?.features) ? profile.features : [];
  const packageAllows =
    Boolean(profile?.allow_onbuy) ||
    features.includes('allow_onbuy') ||
    (Array.isArray(profile?.selected_add_ons) &&
      profile.selected_add_ons.some(
        (a: string | { status?: string; feature_key?: string; addon_code?: string }) => {
          if (typeof a === 'string') return a === 'allow_onbuy';
          if (a?.status && a.status !== 'Active') return false;
          return a?.feature_key === 'allow_onbuy' || a?.addon_code === 'allow_onbuy';
        },
      ));

  const { data: status, refetch } = useQuery({
    queryKey: ['onbuyStatus'],
    queryFn: async () => (await onbuyApi.getStatus()).data.message,
    enabled: packageAllows,
  });

  const [form, setForm] = useState({
    enable_onbuy: false,
    onbuy_seller_id: '',
    onbuy_seller_entity_id: '',
    onbuy_site_id: ONBUY_UK_SITE_ID,
    onbuy_mode: 'Live',
    onbuy_consumer_key: '',
    onbuy_secret_key: '',
    onbuy_sync_products_mode: 'Selected',
    onbuy_auto_confirm_orders: false,
    onbuy_auto_fetch_orders: false,
  });

  useEffect(() => {
    if (!profile) return;
    setForm((f) => ({
      ...f,
      enable_onbuy: Boolean(profile.enable_onbuy),
      onbuy_seller_id: profile.onbuy_seller_id || '',
      onbuy_seller_entity_id: profile.onbuy_seller_entity_id || '',
      onbuy_site_id: profile.onbuy_site_id || ONBUY_UK_SITE_ID,
      onbuy_mode: profile.onbuy_mode || 'Live',
      onbuy_consumer_key: profile.onbuy_consumer_key || '',
      onbuy_sync_products_mode: profile.onbuy_sync_products_mode || 'Selected',
      onbuy_auto_confirm_orders: Boolean(profile.onbuy_auto_confirm_orders),
      onbuy_auto_fetch_orders: Boolean(profile.onbuy_auto_fetch_orders),
    }));
  }, [profile]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = { ...form };
      if (!form.onbuy_secret_key) delete payload.onbuy_secret_key;
      await authApi.updateCompanyProfile(payload);
    },
    onSuccess: () => {
      toast.success('OnBuy settings saved');
      qc.invalidateQueries({ queryKey: ['profile'] });
      void refetch();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || e.message || 'Save failed'),
  });

  const run = async (fn: () => Promise<any>, okText: string) => {
    setMsg(null);
    try {
      const res = await fn();
      const text = typeof res === 'string' ? res : res?.message || okText;
      setMsg({ ok: true, text: typeof text === 'string' ? text : okText });
      void refetch();
      qc.invalidateQueries({ queryKey: ['profile'] });
    } catch (e: any) {
      setMsg({ ok: false, text: e?.response?.data?.message || e.message || 'Failed' });
    }
  };

  const copySite = async () => {
    try {
      await navigator.clipboard.writeText(ONBUY_UK_SITE_ID);
      setCopied('site');
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error('Could not copy');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!packageAllows) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900 space-y-3 max-w-xl">
        <p className="font-medium">OnBuy add-on required</p>
        <p>Contact Support to purchase the OnBuy integration for your company.</p>
        <Link
          to="/support?subject=Enable%20OnBuy%20integration"
          className="btn-primary text-sm w-fit inline-flex"
        >
          Contact Support
        </Link>
      </div>
    );
  }

  const connected = Boolean(status?.connected);

  return (
    <div className="space-y-5 max-w-3xl min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center">
            <ShoppingBag className="w-5 h-5 text-violet-700" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">OnBuy</h2>
            <p className="text-sm text-gray-500">UK marketplace (Site ID {ONBUY_UK_SITE_ID}) — listings, stock and orders</p>
          </div>
        </div>
        <span
          className={`text-xs font-medium px-2.5 py-1 rounded-full ${
            connected ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'
          }`}
        >
          {connected ? 'Connected' : status?.connection_status || 'Disconnected'}
        </span>
      </div>

      {msg && (
        <div
          className={`rounded-lg border px-3 py-2 text-sm flex gap-2 ${
            msg.ok ? 'border-emerald-100 bg-emerald-50 text-emerald-900' : 'border-red-100 bg-red-50 text-red-800'
          }`}
        >
          {msg.ok ? (
            <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          )}
          <span>{msg.text}</span>
        </div>
      )}

      <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50/80 to-white overflow-hidden min-w-0">
        <button
          type="button"
          onClick={() => setGuideOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left min-w-0"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-violet-950 min-w-0">
            <BookOpen className="w-4 h-4 shrink-0" />
            <span className="truncate">How to get credentials from OnBuy</span>
          </span>
          {guideOpen ? (
            <ChevronUp className="w-4 h-4 text-violet-800 shrink-0" />
          ) : (
            <ChevronDown className="w-4 h-4 text-violet-800 shrink-0" />
          )}
        </button>

        {guideOpen && (
          <div className="px-4 pb-4 space-y-4 text-sm text-gray-700 min-w-0 overflow-x-hidden">
            <p className="text-gray-600 break-words">
              Collect Consumer Key, Secret Key, Seller ID, Entity ID and Site ID from the OnBuy Seller Control
              Panel — then paste them below.
            </p>

            <div className="rounded-lg border border-violet-200 bg-white px-3 py-2.5 text-xs text-violet-950 min-w-0">
              <p className="font-semibold mb-1">Path to the form</p>
              <p className="font-mono text-[12px] tracking-tight leading-relaxed break-words whitespace-normal">
                Seller Control Panel →{' '}
                <span className="font-bold underline decoration-violet-600/40">Listings</span>
                {' '}→ Imports &amp; Integrations →{' '}
                <span className="font-bold">OnBuy API</span>
              </p>
              <p className="mt-1.5 text-gray-600 break-words">
                API docs:{' '}
                <a
                  href="https://docs.api.onbuy.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-violet-800 underline break-all"
                >
                  docs.api.onbuy.com
                </a>
              </p>
            </div>

            <p className="text-sm font-semibold text-gray-900">On the OnBuy Seller Control Panel</p>
            <ol className="space-y-3 list-decimal list-inside">
              <li>
                <span className="font-medium text-gray-900">Sign in</span>
                <p className="mt-1 ml-5 text-gray-600">
                  Open your OnBuy seller account / Seller Control Panel.
                </p>
              </li>
              <li>
                <span className="font-medium text-gray-900">Open OnBuy API</span>
                <p className="mt-1 ml-5 text-gray-600">
                  Go to <strong>Listings → Imports &amp; Integrations</strong> (sometimes under Inventory →
                  Integrations). Click <strong>OnBuy API</strong> and enable it if asked.
                </p>
                <div className="mt-2 ml-5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-950 flex gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    If the API page is missing, raise a ticket with OnBuy support asking them to enable API
                    access for your seller account.
                  </span>
                </div>
              </li>
              <li>
                <span className="font-medium text-gray-900">Copy connection details</span>
                <p className="mt-1 ml-5 text-gray-600">From Connection Details / Enabled Integrations copy:</p>
                <div className="mt-2 ml-5 rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs space-y-1">
                  <p>
                    <span className="text-gray-500">Consumer Key →</span> <code>ck_live_…</code> or{' '}
                    <code>ck_test_…</code>
                  </p>
                  <p>
                    <span className="text-gray-500">Secret Key →</span> <code>sk_live_…</code> or{' '}
                    <code>sk_test_…</code>
                  </p>
                  <p>
                    <span className="text-gray-500">Seller ID / Seller Entity ID →</span> matching fields
                  </p>
                  <p>
                    <span className="text-gray-500">Site ID →</span> UK ={' '}
                    <code className="font-semibold">{ONBUY_UK_SITE_ID}</code>
                  </p>
                </div>
                <div className="mt-2 ml-5 flex flex-col sm:flex-row gap-2 min-w-0">
                  <code className="min-w-0 flex-1 text-[11px] bg-white border border-violet-100 rounded px-2 py-1.5">
                    {ONBUY_UK_SITE_ID}
                  </code>
                  <button
                    type="button"
                    onClick={() => void copySite()}
                    className="btn-secondary px-2 py-1.5 text-xs inline-flex items-center justify-center gap-1 shrink-0"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy UK Site ID
                  </button>
                </div>
                {copied === 'site' && <p className="ml-5 text-xs text-emerald-600 mt-1">Copied</p>}
              </li>
              <li>
                <span className="font-medium text-gray-900">Match Live / Test mode</span>
                <p className="mt-1 ml-5 text-gray-600">
                  Use <strong>Live</strong> keys with Live mode (and Test with Test). Mixing modes causes
                  invalid credentials errors.
                </p>
                <div className="mt-2 ml-5 rounded-lg border border-emerald-200 bg-emerald-50/80 p-3 text-xs text-emerald-950 space-y-2">
                  <p className="font-semibold">Paste into SaleDesk (below)</p>
                  <ol className="list-decimal list-inside space-y-1.5 text-gray-800">
                    <li>Consumer Key → <strong>Consumer Key</strong></li>
                    <li>Secret Key → <strong>Secret Key</strong></li>
                    <li>Seller / Entity / Site IDs</li>
                    <li>Save, then <strong>Test connection</strong></li>
                  </ol>
                </div>
              </li>
            </ol>

            <div className="rounded-lg bg-white/80 border border-violet-100 p-3 text-xs text-gray-600 space-y-1">
              <p className="font-medium text-gray-800">Tips</p>
              <p>• Keep SaleDesk SKUs aligned with OnBuy listing SKUs.</p>
              <p>• OnBuy access tokens expire in ~15 minutes — Test connection fetches a fresh one.</p>
              <p>• Start with Selected sync before All active.</p>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 space-y-4">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            checked={form.enable_onbuy}
            onChange={(e) => setForm({ ...form, enable_onbuy: e.target.checked })}
          />
          Enable OnBuy integration
        </label>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-600">API Mode</label>
            <select
              className="input-field mt-1"
              value={form.onbuy_mode}
              onChange={(e) => setForm({ ...form, onbuy_mode: e.target.value })}
            >
              <option value="Live">Live</option>
              <option value="Test">Test</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Site ID</label>
            <input
              className="input-field mt-1"
              value={form.onbuy_site_id}
              onChange={(e) => setForm({ ...form, onbuy_site_id: e.target.value })}
              placeholder={ONBUY_UK_SITE_ID}
            />
            <p className="text-[11px] text-gray-500 mt-1">UK default: {ONBUY_UK_SITE_ID}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Seller ID</label>
            <input
              className="input-field mt-1"
              value={form.onbuy_seller_id}
              onChange={(e) => setForm({ ...form, onbuy_seller_id: e.target.value })}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Seller Entity ID</label>
            <input
              className="input-field mt-1"
              value={form.onbuy_seller_entity_id}
              onChange={(e) => setForm({ ...form, onbuy_seller_entity_id: e.target.value })}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Product sync mode</label>
            <select
              className="input-field mt-1"
              value={form.onbuy_sync_products_mode}
              onChange={(e) => setForm({ ...form, onbuy_sync_products_mode: e.target.value })}
            >
              <option value="Selected">Selected (Show on OnBuy)</option>
              <option value="All active">All active</option>
              <option value="Manual">Manual only</option>
            </select>
          </div>
          <div className="flex flex-col gap-2 justify-end pb-1">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.onbuy_auto_fetch_orders}
                onChange={(e) => setForm({ ...form, onbuy_auto_fetch_orders: e.target.checked })}
              />
              Auto-fetch orders
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.onbuy_auto_confirm_orders}
                onChange={(e) => setForm({ ...form, onbuy_auto_confirm_orders: e.target.checked })}
              />
              Auto-confirm mapped orders
            </label>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-4 space-y-3">
          <p className="text-sm font-medium text-gray-800">API keys</p>
          <p className="text-xs text-gray-500">
            From Seller Control Panel → OnBuy API. Never share the Secret Key in chat.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-gray-600">Consumer Key</label>
              <input
                className="input-field mt-1"
                value={form.onbuy_consumer_key}
                onChange={(e) => setForm({ ...form, onbuy_consumer_key: e.target.value })}
                placeholder="ck_live_… or ck_test_…"
                autoComplete="off"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-gray-600">
                Secret Key {profile?.has_onbuy_secret_key ? '(saved)' : ''}
              </label>
              <div className="relative mt-1">
                <input
                  className="input-field pr-10"
                  type={showSecret ? 'text' : 'password'}
                  value={form.onbuy_secret_key}
                  placeholder={profile?.has_onbuy_secret_key ? '••••••••' : 'sk_live_… or sk_test_…'}
                  onChange={(e) => setForm({ ...form, onbuy_secret_key: e.target.value })}
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                  onClick={() => setShowSecret((v) => !v)}
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <button
            type="button"
            className="btn-primary text-sm inline-flex items-center gap-1.5"
            disabled={saveMut.isPending}
            onClick={() => saveMut.mutate()}
          >
            {saveMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={() =>
              void run(async () => {
                const r = await onbuyApi.testConnection();
                return r.data.message;
              }, 'Connection OK')
            }
          >
            Test connection
          </button>
          <button
            type="button"
            className="btn-secondary text-sm inline-flex items-center gap-1.5 text-red-700"
            onClick={() =>
              void run(async () => {
                await onbuyApi.disconnect();
                setForm((f) => ({ ...f, onbuy_secret_key: '' }));
                return { message: 'Disconnected' };
              }, 'Disconnected')
            }
          >
            <Unplug className="w-3.5 h-3.5" />
            Disconnect
          </button>
          <Link to="/channel-orders?channel=onbuy" className="btn-secondary text-sm w-fit">
            Marketplace Orders
          </Link>
        </div>

        <p className="text-xs text-gray-500">
          Catalogue / order sync will use these Seller API keys. Confirm Test connection succeeds before
          enabling auto-fetch.
        </p>
      </div>
    </div>
  );
}
